import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { BookOpen, Film, HelpCircle, ArrowLeft, ArrowRight, CheckCircle2, Sparkles, BookMarked, ImageOff, Clock, Lock, Star, LayoutGrid, Mic, RotateCcw, Coffee } from "lucide-react";
import AskHikmahVerses from "@/components/AskHikmahVerses";
import ReflectionPanel from "@/components/ReflectionPanel";
import { AuthedImage, AuthedVideo } from "@/components/AuthedMedia";
import { detectMedia } from "@/lib/media";
import { markFirstStoryDone } from "@/lib/pwa";
import FullscreenFrame from "@/components/FullscreenFrame";

// One brand colour per day so the week reads as a rhythm, not one flat strip.
const DAY_COLORS = {
  1: { border: "border-hv-coral", tint: "bg-hv-coralTint", solid: "bg-hv-coral text-white", text: "text-hv-coral" },
  2: { border: "border-hv-teal", tint: "bg-hv-tealTint", solid: "bg-hv-teal text-white", text: "text-hv-teal" },
  3: { border: "border-hv-gold", tint: "bg-hv-goldTint", solid: "bg-hv-gold text-hv-navy", text: "text-[#B27E0A]" },
  4: { border: "border-hv-navy", tint: "bg-hv-navyTint", solid: "bg-hv-navy text-white", text: "text-hv-navy" },
  5: { border: "border-hv-coral", tint: "bg-hv-coralTint", solid: "bg-hv-coral text-white", text: "text-hv-coral" },
  6: { border: "border-hv-gold", tint: "bg-hv-goldTint", solid: "bg-hv-gold text-hv-navy", text: "text-[#B27E0A]" },
  7: { border: "border-hv-slate", tint: "bg-[#F7F4EA]", solid: "bg-hv-slate text-white", text: "text-hv-slate" },
};

// Fisher-Yates — a fresh shuffle every quiz attempt, not just once per page load.
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const QUIZ_CATEGORY_LABELS = {
  modern_story: "From the Story",
  quranic_incident: "Qur'anic Incident",
  moral: "Moral Lesson",
  ayah: "Ayah of the Day",
  word_of_day: "Word of the Day",
};

function formatQuizTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Shrinks its content to fit the space it is given, so a book page never
// scrolls. Used for the storybook's reflections and back-cover text.
function FitScale({ children, resetKey }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [fit, setFit] = useState({ scale: 1, width: null });
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;
    const measure = () => {
      const availH = outer.clientHeight;
      const availW = outer.clientWidth;
      if (!availH || !availW) return;
      // The content is shown at width availW / scale, so the height is measured
      // at that same width. Repeated until the scale settles, so the text fills
      // the page without leaving a gap.
      const prevTransform = inner.style.transform;
      const prevWidth = inner.style.width;
      const heightAt = (w) => {
        inner.style.transform = "none";
        inner.style.width = w + "px";
        const h = inner.scrollHeight;
        return h;
      };
      let scale = Math.min(1, availH / heightAt(availW));
      for (let i = 0; i < 5; i++) {
        const next = Math.min(1, availH / heightAt(availW / scale));
        if (Math.abs(next - scale) < 0.005) break;
        scale = next;
      }
      inner.style.transform = prevTransform;
      inner.style.width = prevWidth;
      setFit({ scale, width: availW });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [resetKey]);
  return (
    <div ref={outerRef} style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}>
      <div
        ref={innerRef}
        style={{
          transform: "scale(" + fit.scale + ")",
          transformOrigin: "top left",
          width: fit.width ? fit.width / fit.scale : "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Each story gets its own tint for the page, the way each Poem does. The set
// cycles, so neighbouring stories never share a colour. Title and card colours
// stay the Stories palette.
const STORY_THEMES = [
  { band: "#E6F7EF" }, // mint
  { band: "#FCE9EE" }, // rose
  { band: "#E7F1FB" }, // sky
  { band: "#FBF3D9" }, // gold
  { band: "#EFE8FA" }, // lavender
  { band: "#FDEBE3" }, // peach
];

// Splits one page of story text into paragraphs for the book. Blank lines in
// the stored text are kept as they are. Otherwise the page's sentences are
// grouped into up to three paragraphs, as in the reference storybook.
function splitIntoParagraphs(text) {
  const blocks = text.split(/\n\n+/).filter((b) => b.trim());
  if (blocks.length > 1) return blocks;
  const sentences = (text.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g) || []).map((x) => x.trim()).filter(Boolean);
  if (sentences.length <= 1) return [text.trim()];
  const groups = Math.min(3, sentences.length);
  const out = [];
  let start = 0;
  for (let g = 0; g < groups; g++) {
    const end = Math.round(((g + 1) * sentences.length) / groups);
    out.push(sentences.slice(start, end).join(" "));
    start = end;
  }
  return out;
}

// Story text for one page. It stays inside the page with no scrolling: if the
// paragraphs are a little too long, the text steps down in small sizes until
// they fit (never below 14px).
function FitBody({ paragraphs, bodyStyle, isUrdu }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const base = isUrdu ? 18 : bodyStyle.fontSize;
    let fs = base;
    el.style.fontSize = fs + "px";
    el.style.gap = "16px";
    while (el.scrollHeight > el.clientHeight + 1 && fs > 14) {
      fs -= 0.5;
      el.style.fontSize = fs + "px";
      if (fs <= 15) el.style.gap = "10px";
    }
  }, [paragraphs, bodyStyle, isUrdu]);
  return (
    <div ref={ref} className="flex-1 min-h-0 overflow-hidden flex flex-col" style={{ marginTop: 26 }}>
      {paragraphs.map((para, pi) => (
        <p key={pi} className={isUrdu ? "font-urdu" : undefined} style={{ ...bodyStyle, fontSize: "inherit" }}>
          {para}
        </p>
      ))}
    </div>
  );
}

export default function StoryDetail() {
  const { seq } = useParams();
  const [sp] = useSearchParams();
  const initialTab = sp.get("tab") || "video";
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, getLoc, lang } = useLang();
  const [story, setStory] = useState(null);
  // The story tint covers the header and the top half of the Days strip, and the
  // rest of the page stays white. The strip is measured so the tint stops at its middle.
  const stripRef = useRef(null);
  const [bandH, setBandH] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const el = stripRef.current;
      if (el) setBandH(el.offsetTop + el.offsetHeight / 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stripRef.current) ro.observe(stripRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [story]);
  const [isPreview, setIsPreview] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState(initialTab);
  // Sub-tab inside "Learn" — keeps the top-level nav at 3 tabs while still
  // separating morals/ayah from the quiz instead of one long stacked scroll.
  const [learnSubTab, setLearnSubTab] = useState("morals");
  const [page, setPage] = useState(0);
  const [isTurning, setIsTurning] = useState(false);
  // Index of the one sheet physically mid-turn right now. Needed because
  // the resting z-index order (computed straight from `flipped`) puts the
  // page being revealed above the page being turned the instant you click
  // — the turn was playing correctly underneath the whole time, but the
  // new page's higher resting z-index covered it from frame one, so no
  // motion was ever visible. The turning sheet gets pinned to the very top
  // for the ~750ms transition, then this clears and resting order takes
  // over again — same fix the Little Verses reader's flipbook uses
  // (raise z-index to 100 for the active turn, reset once it settles).
  const [turningIndex, setTurningIndex] = useState(null);
  // Mounting every page's image at once can flood the backend with
  // concurrent GridFS streams for a long storybook — a real bug hit while
  // porting this flip mechanism from the Little Verses reader. Pages get a
  // real image src only a couple ahead of wherever the child currently is;
  // once set it's never unset.
  const [loadedUpTo, setLoadedUpTo] = useState(2);
  // The storybook is laid out at the reference book's own size (900x560 for a
  // spread, 450x560 for one page) and scaled to fit its box. Text, spacing and
  // fonts are therefore identical on desktop, mobile and full screen. Phones
  // show a single full-width page at 1:1, the same as the reference book.
  const bookBoxRef = useRef(null);
  const [bookBox, setBookBox] = useState({ single: false, designW: 900, scale: 1, height: 560 });
  useEffect(() => {
    const el = bookBoxRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = (el.parentElement || el).clientWidth;
      if (!w) return;
      // Never larger than the reference book (1:1) on the page itself; only
      // full screen enlarges it to fill the screen.
      if (w >= 700) {
        const holder = el.parentElement || el;
        // The full-screen window is position:fixed, so it may grow past 900px.
        const s = holder.style.position === "fixed" ? w / 900 : Math.min(1, w / 900);
        setBookBox({ single: false, designW: 900, scale: s, height: 560 * s });
      }
      else setBookBox({ single: true, designW: w, scale: 1, height: (560 * w) / 450 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el.parentElement || el);
    return () => ro.disconnect();
  }, [tab, story]);
  // Quiz: one question at a time, answer options reshuffled on every attempt,
  // a running timer while it's in progress, and immediate green/red feedback
  // the instant an option is tapped (instead of the old "answer everything,
  // then Check Answers" single-page form).
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizShuffled, setQuizShuffled] = useState([]);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizPicked, setQuizPicked] = useState(null); // shuffled-position index picked for the current question
  const [quizAnswers, setQuizAnswers] = useState([]); // original-option indices collected so far, in question order
  const [quizElapsed, setQuizElapsed] = useState(0);
  const [quizResult, setQuizResult] = useState(null);
  // The answer key is deliberately stripped out of GET /stories/{seq} (so it
  // never sits in the Network tab before it's earned) — this holds the one
  // correct_index /quiz/check reveals for the question just answered, not
  // the whole quiz's key up front.
  const [quizRevealedCorrect, setQuizRevealedCorrect] = useState(null);
  const [stepsDone, setStepsDone] = useState({});

  useEffect(() => {
    if (!user || isPreview) return;
    client.get(`/stories/${seq}/steps`).then(({ data }) => setStepsDone(data.steps || {})).catch(() => {});
  }, [seq, user, isPreview]);

  // Marks one day's step complete, then moves to the next day's content.
  const markStep = async (step, go) => {
    try {
      const { data } = await client.post(`/stories/${seq}/steps/${step}`);
      setStepsDone(data.steps || {});
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
      return;
    }
    if (go) go();
  };

  useEffect(() => {
    setStory(null);
    setErr("");
    client.get(`/stories/${seq}`).then(({ data }) => {
      setStory(data.story);
      setIsPreview(!!data.preview);
    }).catch((ex) => {
      // Signed-out visitor hit a seq that isn't the free preview — same
      // outcome ProtectedRoute used to give (bounce to /login), just decided
      // here now that the route itself is public, so /story/1 can stay
      // reachable with no auth check at all for the landing page's
      // "Try free" button.
      if (ex.response?.status === 401 && user === false) {
        navigate(`/login?next=${encodeURIComponent(`/story/${seq}`)}`, { replace: true });
        return;
      }
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    });
  }, [seq, user, navigate]);

  useEffect(() => {
    // Tracks a page (illustration) index now, not a sheet index — cover and
    // back-cover need no eager-load of their own, they're always shown.
    const total = story?.storybook?.length || 0;
    const needed = Math.min(page + 1, Math.max(total - 1, 0));
    setLoadedUpTo((prev) => (needed > prev ? needed : prev));
  }, [page, story]);

  useEffect(() => {
    if (!quizStarted || quizResult) return;
    const id = setInterval(() => setQuizElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [quizStarted, quizResult]);

  const startQuiz = () => {
    const shuffled = (story.quiz || []).map((q) => ({
      ...q,
      shuffledOptions: shuffleArray((q.options || []).map((text, origIndex) => ({ text, origIndex }))),
    }));
    setQuizShuffled(shuffled);
    setQuizIndex(0);
    setQuizPicked(null);
    setQuizAnswers([]);
    setQuizElapsed(0);
    setQuizResult(null);
    setQuizRevealedCorrect(null);
    setQuizStarted(true);
  };

  const pickQuizOption = async (pos) => {
    if (quizPicked !== null) return;
    setQuizPicked(pos);
    const q = quizShuffled[quizIndex];
    const origIndex = q.shuffledOptions[pos].origIndex;
    const nextAnswers = [...quizAnswers, origIndex];
    setQuizAnswers(nextAnswers);
    // The reveal is cosmetic (which option glows green) — a network hiccup
    // here must never block the quiz itself, which is scored authoritatively
    // by the final /quiz submit below regardless of whether this succeeded.
    try {
      const { data } = await client.post(`/stories/${seq}/quiz/check`, {
        question_index: quizIndex,
        answer_index: origIndex,
      });
      setQuizRevealedCorrect(data.correct_index);
    } catch {
      setQuizRevealedCorrect(undefined); // distinct from null: "tried, got nothing" — still no green shown
    }
    setTimeout(async () => {
      if (quizIndex + 1 < quizShuffled.length) {
        setQuizIndex((i) => i + 1);
        setQuizPicked(null);
        setQuizRevealedCorrect(null);
      } else {
        try {
          const { data } = await client.post(`/stories/${seq}/quiz`, { story_id: story.id, answers: nextAnswers });
          setQuizResult(data);
          markFirstStoryDone();
        } catch (ex) {
          // No connection: the answers are saved on this device and sent when
          // the connection returns (see src/lib/offlineQueue.js).
          if (ex.queued) {
            setQuizResult({ queued: true });
            markFirstStoryDone();
          } else {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
          }
        }
      }
    }, 900);
  };

  if (err) return <div className="p-12 hv-card mx-8 my-8" data-testid="story-error">{err}</div>;
  if (!story) return <div className="p-12 text-hv-slate">Loading…</div>;

  const pages = story.storybook || [];
  const hasCover = !!story.storybook_cover || !!story.cover_image_gridfs_id;
  // A real physical flipbook: one illustration-only page and one text-only
  // page per storybook entry, alternating — matching the reference flipbook
  // this was rebuilt against, instead of showing an entry's image and text
  // side by side in a single combined spread. Each "sheet" is one physical
  // leaf with two faces: front (showing until turned) and back (revealed
  // once turned, becoming the new left-hand page of the open spread) —
  // sheet 1's back is illustration 1, sheet 2's front is text 1 (the two
  // form one visible "spread" once sheet 1 is turned), sheet 2's back is
  // illustration 2, and so on, ending on a plain back-cover plate.
  const sheets = [];
  if (hasCover) {
    if (pages.length === 0) {
      sheets.push({ front: { type: "cover" }, back: { type: "backcover" } });
    } else {
      sheets.push({ front: { type: "cover" }, back: { type: "illustration", idx: 0 } });
      for (let k = 1; k < pages.length; k++) {
        sheets.push({ front: { type: "text", idx: k - 1 }, back: { type: "illustration", idx: k } });
      }
      sheets.push({ front: { type: "text", idx: pages.length - 1 }, back: { type: "backcover" } });
    }
  } else {
    for (let k = 0; k < pages.length; k++) {
      sheets.push({ front: { type: "illustration", idx: k }, back: { type: "text", idx: k } });
    }
  }
  const totalSheets = sheets.length;
  const safePage = Math.min(page, totalSheets);

  const goNext = () => {
    if (isTurning || safePage >= totalSheets) return;
    setIsTurning(true);
    setTurningIndex(safePage); // sheet safePage flips false -> true
    setPage(safePage + 1);
  };
  const goPrev = () => {
    if (isTurning || safePage <= 0) return;
    setIsTurning(true);
    setTurningIndex(safePage - 1); // sheet safePage-1 flips true -> false
    setPage(safePage - 1);
  };
  const handleBookClick = (e) => {
    if (isTurning) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    // A real two-page spread: left half is the already-turned page, right
    // half the next one to turn — an even 50/50 split, not the left-third
    // compromise the old single-slide-per-page layout needed.
    if (clickX < rect.width / 2) goPrev(); else goNext();
  };
  const handleSheetTransitionEnd = (e) => {
    if (e.propertyName === "transform") {
      setIsTurning(false);
      setTurningIndex(null);
    }
  };

  // Face renderers — a sheet's front/back is one of these four kinds.
  // Defined here (not hooks, plain closures) so they can read `story`,
  // `pages`, `loadedUpTo` etc. straight from the render scope.
  const renderCoverFace = () => {
    const cover = story.storybook_cover;
    const src = story.cover_image_gridfs_id ? `/api/stories/${seq}/media/cover-image` : cover?.image_url;
    return (
      <div className="relative h-full rounded-2xl overflow-hidden flex flex-col items-center justify-end text-center px-8 py-8 border-4 border-double border-hv-gold bg-gradient-to-br from-hv-navy to-hv-teal">
        {/* object-contain, not object-cover: finished cover art (e.g. Story
            1's) is portrait, and now that this face is half the book's width
            it's already a close match — contain guarantees nothing (like a
            baked-in title) ever gets cropped regardless of the exact photo. */}
        {src ? (
          <AuthedImage src={src} className="absolute inset-0 w-full h-full object-contain" />
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-hv-navy via-hv-navy/40 to-transparent" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-hv-gold/90">
                HikmahVerses Presents
              </div>
              <div className="hv-heading text-white text-2xl mt-3 max-w-[90%] mx-auto"
                   style={{ textShadow: "0 2px 6px rgba(0,0,0,0.45)" }}>
                {getLoc(cover?.title)}
              </div>
              {cover?.subtitle && (
                <p className="mt-3 text-white/85 text-base leading-relaxed max-w-[90%] mx-auto">
                  {getLoc(cover.subtitle)}
                </p>
              )}
              {cover?.branding && (
                <div className="mt-5 text-[11px] uppercase tracking-[0.2em] font-bold text-hv-gold">
                  {getLoc(cover.branding)}
                </div>
              )}
            </div>
          </>
        )}
        <div className="relative mt-4 text-xs font-bold text-white/70 animate-pulse"
             style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
          Tap to open the book ➔
        </div>
      </div>
    );
  };

  const renderBackCoverFace = () => {
    const bc = story.back_cover;
    // Plain closing plate when a story has no back-cover copy of its own yet
    // (most stories, for now) — matches the cover's own gradient fallback
    // treatment so the two bookends read as a pair.
    if (!bc) {
      return (
        <div className="relative h-full rounded-2xl overflow-hidden flex flex-col items-center justify-center text-center px-8 py-8 border-4 border-double border-hv-gold bg-gradient-to-br from-hv-navy to-hv-teal">
          <div className="text-hv-gold text-xs uppercase tracking-[0.3em] font-bold">✦ The End ✦</div>
          <div className="hv-heading text-white text-2xl mt-3 max-w-[85%]">{getLoc(story.title)}</div>
          <div className="mt-6 text-[11px] uppercase tracking-[0.2em] font-bold text-hv-gold/90">
            600 Stories Series · By HikmahVerses
          </div>
        </div>
      );
    }
    // The reference book's actual back-cover jacket copy — a hook headline,
    // a drop-capped summary, an editorial endorsement, and a publisher/
    // brand/ISBN strip, matching its own layout almost element-for-element.
    return (
      <div className="relative h-full flex flex-col"
           style={{ background: "linear-gradient(180deg, #FFFDF9 0%, #FAF5EC 50%, #F5EDE0 100%)", border: "2px solid rgba(212,175,55,0.4)", padding: "22px 26px", color: "#15274D" }}
           data-testid="story-back-cover">
        <div className="shrink-0" style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "Cinzel, serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "#B7791F" }}>
            600 Stories Series · Story {story.story_number_in_juz}
          </div>
          <div style={{ fontFamily: "Cinzel, serif", marginTop: 4, fontSize: 16, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", lineHeight: 1.3, whiteSpace: "pre-line" }}>
            {getLoc(bc.headline)}
          </div>
        </div>

        <FitScale resetKey={"back-" + seq}>
        <div className="flex flex-col" style={{ marginTop: 16, gap: 10, fontFamily: '"EB Garamond", Georgia, serif' }}>
          {bc.summary.map((para, i) => {
            const text = getLoc(para);
            return (
              <p key={i} style={{ fontSize: 14, lineHeight: 1.5, textAlign: "justify", margin: 0 }}>
                {i === 0 ? (
                  <>
                    <span style={{ float: "left", fontFamily: "Cinzel, serif", fontSize: 40, fontWeight: 800, lineHeight: 0.8, paddingRight: 6, paddingTop: 4, color: "#B7791F" }}>
                      {text.charAt(0)}
                    </span>
                    {text.slice(1)}
                  </>
                ) : text}
              </p>
            );
          })}
        </div>
        </FitScale>

        {bc.endorsement && (
          <div className="shrink-0 text-center" style={{ marginTop: 12, background: "rgba(212,175,55,0.08)", borderLeft: "2.5px solid #D4AF37", borderRight: "2.5px solid #D4AF37", borderRadius: 6, padding: "8px 16px" }}>
            <p style={{ fontFamily: '"EB Garamond", Georgia, serif', fontStyle: "italic", fontSize: 13, color: "#1E3A8A", margin: 0 }}>"{getLoc(bc.endorsement)}"</p>
            {bc.endorsement_author && (
              <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: "#5B6882" }}>— {getLoc(bc.endorsement_author)}</div>
            )}
          </div>
        )}

        <div className="shrink-0 flex items-end justify-between gap-2" style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid rgba(212,175,55,0.3)", fontSize: 10, color: "#5B6882", lineHeight: 1.4 }}>
          <div>
            <div style={{ fontWeight: 700, color: "#15274D" }}>600 Stories Series · Book {story.story_number_in_juz}</div>
            <div>A Children's Islamic Adventure</div>
            <div>Printed for Young Believers Worldwide</div>
            <div style={{ color: "#B7791F", fontWeight: 700 }}>stories.hikmahverses.com</div>
          </div>
          <div className="text-center shrink-0" style={{ color: "#B7791F" }}>
            <div style={{ fontSize: 14 }}>❖ ۞ ❖</div>
            <div style={{ fontFamily: "Cinzel, serif", fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", color: "#15274D" }}>600 STORIES SERIES</div>
          </div>
        </div>
      </div>
    );
  };

  // The reference book's "Sacred Reflections & Prophetic Morals" page, copied
  // from its style.css values (px, Fredoka body, Cinzel/Amiri/EB Garamond
  // where the book uses them). Pulled from the story's own fields; falls back
  // to the flowing-paragraph page when a story has none of this yet.
  const renderReflectionsFace = () => {
    const morals = story.morals_rich?.length ? story.morals_rich : null;
    const toneStyle = {
      pink: { background: "#FFF1F2", border: "1px solid #FECDD3" },
      blue: { background: "#F0F9FF", border: "1px solid #BAE6FD" },
      amber: { background: "#FFFBEB", border: "1px solid #FDE68A" },
    };
    const CINZEL = "Cinzel, serif";
    const GARAMOND = '"EB Garamond", Georgia, serif';
    return (
      <div className="relative h-full flex flex-col" style={{ ...PAGE_PAPER, padding: "22px 26px" }} data-testid="story-reflections">
        <div className="shrink-0" style={HEAD_FONT}>{getLoc(story.title)}</div>
        <FitScale resetKey={"refl-" + seq}>
        <div className="flex flex-col" style={{ marginTop: 6, gap: 8 }}>
          {story.conclusion && (
            <p style={{ ...BODY_FONT, fontFamily: "Fredoka, system-ui, sans-serif", fontSize: 14, lineHeight: 1.4, textAlign: "left" }}>{getLoc(story.conclusion)}</p>
          )}
          <div style={{ textAlign: "center", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5C755" }}>
            ✦ Sacred Reflections &amp; Prophetic Morals ✦
          </div>

          {story.ayah && (
            <div style={{ background: "#FFFDF8", border: "1.5px solid rgba(212,168,67,0.5)", borderRadius: 10, padding: 9, textAlign: "center" }}>
              <span style={{ display: "inline-block", background: "linear-gradient(135deg, #D4AF37, #B7791F)", color: "#fff", fontFamily: CINZEL, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 99 }}>
                ✨ Ayah of the Day ✨
              </span>
              <div style={{ fontFamily: '"Amiri", serif', fontSize: 22, color: "#15274D", marginTop: 4, direction: "rtl" }}>{story.ayah.arabic}</div>
              <div style={{ fontFamily: GARAMOND, fontStyle: "italic", fontSize: 13, color: "#5B6882", marginTop: 4 }}>&ldquo;{getLoc(story.ayah.translations)}&rdquo;</div>
              {story.ayah.note && <div style={{ fontSize: 12, color: "#5B6882", marginTop: 2 }}>{getLoc(story.ayah.note)}</div>}
              <div style={{ fontSize: 12, color: "#5B6882", marginTop: 4 }}>{story.ayah.surah} {story.ayah.ayah_number}</div>
            </div>
          )}

          {story.word_of_the_day && (
            <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderLeft: "4px solid #16A34A", borderRadius: 8, padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: "0.04em" }}>🌍 Word of the Day:</span>
              <span>
                <span style={{ fontFamily: '"Amiri", serif', fontSize: 18, fontWeight: 700, color: "#15803D" }}>{story.word_of_the_day.arabic}</span>{" "}
                <span style={{ fontSize: 13, color: "#166534", fontStyle: "italic" }}>{getLoc(story.word_of_the_day.meaning)}</span>
              </span>
            </div>
          )}

          {morals && (
            <div style={{ background: "#F7FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 9 }}>
              <div style={{ textAlign: "center", fontFamily: CINZEL, fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#15274D", marginBottom: 6 }}>
                Three Core Morals From Story {story.story_number_in_juz}
              </div>
              {morals.map((m, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, borderRadius: 8, padding: "5px 9px", fontSize: 12.5, lineHeight: 1.3, marginTop: i === 0 ? 0 : 5, ...(toneStyle[m.tone] || { background: "#FAF5EC", border: "1px solid #E5DFC8" }) }}>
                  <span>{m.icon}</span>
                  <span>
                    <strong style={{ color: "#15274D" }}>{getLoc(m.title)}:</strong>{" "}
                    <span style={{ color: "#5B6882" }}>{getLoc(m.body)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {story.hadith_quote && (
            <div style={{ textAlign: "center", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "7px 11px", fontSize: 12.5, color: "#15274D", lineHeight: 1.35 }}>
              <span style={{ color: "#B7791F", fontWeight: 700 }}>📖 Prophetic Wisdom:</span>{" "}
              <span style={{ color: "#1E3A8A", fontWeight: 500 }}>{getLoc(story.hadith_quote)}</span>
              {(story.hadith_cite || story.references?.[0]?.label) && <span> ({getLoc(story.hadith_cite) || story.references[0].label})</span>}
              <div style={{ marginTop: 4, fontFamily: CINZEL, fontSize: 9, letterSpacing: "0.16em", color: "#5B6882", textTransform: "uppercase" }}>
                Know • Reflect • Practice • Grow
              </div>
            </div>
          )}
        </div>
        </FitScale>
        <div className="shrink-0" style={FOOT_FONT}>{pages.length}</div>
      </div>
    );
  };

  const renderIllustrationFace = (idx) => {
    const p = pages[idx];
    if (!p) return null;
    const loaded = idx <= loadedUpTo;
    const src = p.image_gridfs_id ? `/api/stories/${seq}/media/storybook/${idx}` : p.image_url;
    return (
      <div className="relative h-full flex flex-col items-center justify-center"
           style={{ ...PAGE_PAPER, padding: 20, gap: 10 }}
           data-testid={`story-illustration-${idx}`}>
        <div className="relative flex-1 w-full min-h-0 rounded-xl overflow-hidden border-4 border-white shadow-lg bg-hv-navy">
          {loaded && src && <AuthedImage src={src} className="w-full h-full object-cover" />}
        </div>
        <div className="shrink-0" style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 13, color: "#5B6882" }}>{idx + 1}</div>
      </div>
    );
  };

  // Reference book's exact values (style.css), in px so they scale with the book.
  const PAGE_PAPER = { background: "linear-gradient(to right, #FFFFFF 0%, #FBF6E4 100%)", color: "#15274D", padding: "30px 34px" };
  const HEAD_FONT = { fontFamily: "Cinzel, serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", textAlign: "center", color: "#15274D" };
  // Same body text as the reference storybook: Nunito, 17px, 1.65 line height.
  // Hyphenation keeps justified lines from opening up wide word gaps.
  const BODY_FONT = { fontFamily: 'Nunito, system-ui, sans-serif', fontSize: 17, lineHeight: 1.65, textAlign: "justify", hyphens: "auto", color: "#15274D", margin: 0 };
  const FOOT_FONT = { fontFamily: '"EB Garamond", Georgia, serif', fontSize: 13, color: "#5B6882", marginTop: 10, paddingTop: 8, borderTop: "1px solid #E5DFC8", textAlign: "center" };

  const renderTextFace = (idx) => {
    const p = pages[idx];
    if (!p) return null;
    const isLast = idx === pages.length - 1;
    const hasReflections = story.morals_rich?.length || story.ayah || story.word_of_the_day || story.hadith_quote;
    if (isLast && hasReflections) return renderReflectionsFace();
    const isUrdu = lang === "ur";
    return (
      <div className="relative h-full flex flex-col" style={{ ...PAGE_PAPER }}
           data-testid={`story-text-${idx}`}>
        <div className="shrink-0" style={{ ...HEAD_FONT, fontFamily: isUrdu ? undefined : HEAD_FONT.fontFamily }}>
          {getLoc(story.title)}
        </div>
        {/* Each source paragraph ("\n\n"-separated, set that way by the admin
            upload) gets its own block with real space between. */}
        <FitBody paragraphs={splitIntoParagraphs(getLoc(p.text))} bodyStyle={isUrdu ? { ...BODY_FONT, fontFamily: undefined } : BODY_FONT} isUrdu={isUrdu} />
        <div className="shrink-0" style={{ ...FOOT_FONT, fontFamily: isUrdu ? undefined : FOOT_FONT.fontFamily }}>
          {idx + 1}
        </div>
      </div>
    );
  };

  const renderFace = (face) => {
    if (!face) return null;
    if (face.type === "cover") return renderCoverFace();
    if (face.type === "backcover") return renderBackCoverFace();
    if (face.type === "illustration") return renderIllustrationFace(face.idx);
    if (face.type === "text") return renderTextFace(face.idx);
    return null;
  };

  // Anonymous free-preview visitor: video is the only real tab. Storybook
  // and Learn still switch (so the locked state itself is reachable/
  // explorable) but render a "sign in to unlock" prompt instead of content
  // — the preview API response has already stripped storybook/quiz/morals
  // off the story object entirely, so there's nothing to render anyway.
  const tabLocked = isPreview && tab !== "video";

  const storyTheme = STORY_THEMES[(Math.max(Number(seq) || 1, 1) - 1) % STORY_THEMES.length];

  return (
    <div className="relative min-h-[calc(100vh-73px)]" data-testid="story-theme" data-story-theme={storyTheme.band}>
    <div aria-hidden="true" className="absolute inset-x-0 top-0 pointer-events-none" style={{ height: bandH, backgroundColor: storyTheme.band }} />
    <main className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="story-detail">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-bold text-hv-navy text-base shadow-sm hover:bg-hv-cream transition" data-testid="back-to-today">
          <ArrowLeft size={18} strokeWidth={2.5} /> Back
        </Link>
        <span className="hv-badge" data-testid="story-number-badge">Story #{String(story.global_sequence).padStart(2, "0")}</span>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <div className="bg-white rounded-2xl w-16 h-16 flex items-center justify-center shadow-sm shrink-0">
          <Star size={28} strokeWidth={2.2} className="text-hv-gold" />
        </div>
        <div className="min-w-0">
          <h1 className="hv-heading text-3xl sm:text-4xl text-hv-navy">{getLoc(story.title)}</h1>
          <p className="mt-1 text-sm text-hv-slate">
            Juz {story.juz_number} · Story {story.story_number_in_juz}
            {isPreview && <span className="ms-2 font-bold text-hv-coral" data-testid="story-preview-badge">Free preview</span>}
          </p>
        </div>
      </div>


      {/* This week's lessons — the seven day steps, each opening its tab. */}
      <div ref={stripRef} className="mt-6 hv-card p-4 sm:p-5" data-testid="story-day-strip">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-hv-slate">This week&apos;s lessons</div>
        <div className="mt-3 grid grid-cols-4 sm:grid-cols-7 gap-2">
          {[
            { n: 1, label: "Story", sub: "Video", icon: Film, tab: "video" },
            { n: 2, label: "Story Book", sub: "Read", icon: BookOpen, tab: "storybook" },
            { n: 3, label: "Morals", sub: "& Ayah", icon: LayoutGrid, tab: "learn", sub_tab: "morals" },
            { n: 4, label: "Quiz", sub: "Quiz", icon: HelpCircle, tab: "learn", sub_tab: "quiz" },
            { n: 5, label: "Write", sub: "Your story", icon: Mic, tab: "write" },
            { n: 6, label: "Review", sub: "Coming soon", icon: RotateCcw, tab: null },
            { n: 7, label: "Rest", sub: "Live", icon: Coffee, tab: null },
          ].map((d) => {
            const Icon = d.icon;
            const active = d.tab && tab === d.tab && (!d.sub_tab || learnSubTab === d.sub_tab);
            const go = d.tab ? () => { setTab(d.tab); if (d.sub_tab) setLearnSubTab(d.sub_tab); } : undefined;
            const c = DAY_COLORS[d.n];
            const stepKey = { 1: "video", 2: "storybook", 3: "morals", 4: "quiz", 5: "write" }[d.n];
            const finished = !!stepsDone[stepKey];
            return (
              <button key={d.n} type="button" onClick={go} disabled={!go} data-testid={`story-day-${d.n}`}
                      className={`rounded-xl px-2 py-3 flex flex-col items-center gap-1.5 text-center border-2 transition ${
                        finished ? "border-[#2E9E5B] bg-[#2E9E5B] text-white" : active ? `${c.border} ${c.tint}` : "border-transparent bg-[#F7F4EA]"
                      } ${go ? "hover:shadow-sm" : "opacity-60 cursor-default"}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center ${finished ? "bg-white text-[#2E9E5B]" : active ? c.solid : `bg-white ${c.text}`}`}>
                  {finished ? <CheckCircle2 size={16} strokeWidth={2.6} /> : <Icon size={16} strokeWidth={2.4} />}
                </span>
                <span className={`text-[11px] font-bold uppercase tracking-wider ${finished ? "text-white" : "text-hv-navy"}`}>Day {d.n}</span>
                <span className={`text-xs ${finished ? "text-white/90" : "text-hv-slate"}`}>{d.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Signed-out visitor hit a locked tab on the free-preview story */}
      {tabLocked && (
        <div className="mt-6 hv-card p-8 text-center" data-testid="story-preview-locked">
          <div className="hv-icon-circle w-12 h-12 rounded-full flex items-center justify-center mx-auto">
            <Lock size={20} strokeWidth={2.5} />
          </div>
          <div className="hv-heading text-hv-navy text-xl mt-4">Sign in to unlock the rest</div>
          <p className="mt-2 text-hv-slate text-sm max-w-sm mx-auto">
            The storybook, quiz, morals and Ayah of the Day for this story are free once you create an account — the video preview above is open to everyone.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to={`/login?next=${encodeURIComponent(`/story/${seq}?tab=${tab}`)}`} data-testid="preview-login-cta" className="hv-btn-secondary">
              Sign in
            </Link>
            <Link to="/signup" data-testid="preview-signup-cta" className="hv-btn-primary">
              Create free account
            </Link>
          </div>
        </div>
      )}

      {/* Video */}
      {tab === "video" && (
        <div className="mt-6 hv-card p-6" data-testid="story-video">
          <FullscreenFrame testid="story-video-frame">
          <div className="rounded-2xl overflow-hidden bg-hv-navy aspect-video">
            {story.video_gridfs_id ? (
              <AuthedVideo src={`/api/stories/${seq}/media/video`} className="w-full h-full" />
            ) : (() => {
              const media = detectMedia(story.video?.asset_url);
              if (media.type === "direct" && media.src) {
                return (
                  <video controls className="w-full h-full">
                    <source src={media.src} type="video/mp4" />
                  </video>
                );
              }
              if (media.type !== "none" && media.src) {
                return (
                  <iframe
                    src={media.src}
                    title="Story video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="story-video-iframe"
                  />
                );
              }
              return <div className="w-full h-full flex items-center justify-center text-white">Video coming soon</div>;
            })()}
          </div>
          </FullscreenFrame>
          <p className="mt-4 text-hv-slate text-sm">A 5-minute animated retelling. Vocals-only audio, modest visuals — no depictions of Prophets.</p>
        </div>
      )}

      {/* Storybook — a real two-page-spread flipbook, matching the reference
          flipbook this was rebuilt against: each physical sheet is half the
          book's width, stacked right (unturned) until flipped left (turned),
          a plain CSS class-toggle transition rather than per-frame JS. Every
          sheet is permanently mounted with fixed content, never reassigned —
          confirmed live that reusing one element whose image src gets
          swapped between turns hits a real Chromium compositor bug where the
          image paints blank for the whole rotateY transition; static
          per-sheet elements don't have that problem. Collapses to a single
          full-width page on narrow screens, spine hidden, same as the
          reference's own mobile behaviour. Click the left half of the
          spread (or Prev) to go back, the right half (or Next) to advance. */}
      {tab === "storybook" && !tabLocked && totalSheets > 0 && (
        <div className="mt-6 hv-card p-6" data-testid="story-book">
          <style>{`
            .sb-sheet {
              position: absolute; top: 0; right: 0; bottom: 0; width: 50%;
              transform-style: preserve-3d; transform-origin: left center;
              transform: rotateY(0deg);
              transition: transform 0.75s cubic-bezier(0.15, 0.85, 0.45, 1), box-shadow 0.75s;
            }
            .sb-sheet.sb-flipped { transform: rotateY(-180deg); }
            .sb-face { position: absolute; inset: 0; backface-visibility: hidden; }
            .sb-face.sb-back {
              transform: rotateY(180deg);
              background: linear-gradient(to right, #FBF6E4 0%, #F5EBD0 100%);
            }
            .sb-spine {
              position: absolute; left: 50%; top: 0; bottom: 0; width: 10px;
              transform: translateX(-50%);
              background: linear-gradient(90deg, rgba(0,0,0,0.15) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0.08) 60%, rgba(0,0,0,0.15) 100%);
              box-shadow: inset 0 0 10px rgba(0,0,0,0.25);
              z-index: 200; pointer-events: none;
            }
            .sb-single .sb-sheet { width: 100%; left: 0; right: auto; }
            .sb-single .sb-spine { display: none; }
          `}</style>
          <FullscreenFrame testid="story-book-frame">
          <div ref={bookBoxRef} className="relative" style={{ height: bookBox.height, width: bookBox.single ? "100%" : bookBox.designW * bookBox.scale, margin: "0 auto" }}>
          <div
            className={`sb-book absolute top-0 select-none rounded-2xl overflow-hidden ${bookBox.single ? "sb-single" : ""}`}
            style={{
              width: bookBox.designW, height: 560, left: "50%", transform: `translateX(-50%) scale(${bookBox.scale})`, transformOrigin: "top center",
              perspective: 1800, boxShadow: "0 30px 70px rgba(21,39,77,0.30), 0 10px 20px rgba(21,39,77,0.15)",
              background: "#FBF6E4",
            }}
            onClick={handleBookClick}
            data-testid="story-book-flip-area"
          >
            <div className="sb-spine" />
            {sheets.map((sheet, i) => {
              const flipped = i < safePage;
              const isActive = flipped ? i === safePage - 1 : i === safePage;
              const isTurningThis = i === turningIndex;
              // Globally-ordered (not per-side-relative) so the currently
              // relevant page always wins regardless of position — matters
              // most on the single-page mobile layout, where flipped (left)
              // and unflipped (right) sheets occupy the very same box:
              // unflipped always outranks flipped, so the next page to read
              // is always what's on top there.
              const zIndex = isTurningThis ? totalSheets * 2 + 10 : (flipped ? i + 1 : totalSheets * 2 - i);
              return (
                <div
                  key={i}
                  className={`sb-sheet ${flipped ? "sb-flipped" : ""}`}
                  style={{ zIndex, cursor: isActive ? "pointer" : "default",
                           boxShadow: isTurningThis ? "0 10px 30px rgba(0,0,0,0.35)" : "none" }}
                  onTransitionEnd={isTurningThis ? handleSheetTransitionEnd : undefined}
                  data-testid={`story-sheet-${i}`}
                >
                  <div className="sb-face">{renderFace(sheet.front)}</div>
                  <div className="sb-face sb-back">{renderFace(sheet.back)}</div>
                </div>
              );
            })}
          </div>
          </div>
          </FullscreenFrame>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button disabled={safePage === 0} onClick={goPrev} data-testid="prev-page"
                    className="hv-btn-secondary inline-flex items-center gap-2 disabled:opacity-40">
              <ArrowLeft size={16} /> Prev
            </button>
            <span className="text-xs font-bold text-hv-slate text-center">{safePage} of {totalSheets} pages turned <span className="hidden sm:inline">· tap left/right to turn</span></span>
            <button disabled={safePage >= totalSheets} onClick={goNext} data-testid="next-page"
                    className="hv-btn-primary inline-flex items-center gap-2 disabled:opacity-40">
              Next <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Learn: a sub-tab switcher (Morals & Ayah / Quiz) instead of one long
          stacked scroll, while the top-level nav stays at 3 tabs. */}
      {tab === "write" && !tabLocked && <ReflectionPanel seq={seq} />}

      {tab === "learn" && !tabLocked && (
        <div className="mt-6" data-testid="story-learn">

      {learnSubTab === "morals" && (
        <div className="mt-4 space-y-6" data-testid="story-morals">
          {/* Morals */}
          <div className="hv-card p-6">
            <span className="hv-badge">{t("morals")}</span>
            <ul className="mt-4 space-y-3">
              {(story.morals || []).map((m, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`moral-${i}`}>
                  <div className="hv-icon-circle-gold w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle2 size={18} strokeWidth={2.5} />
                  </div>
                  <div className="hv-heading text-hv-navy text-lg">{getLoc(m)}</div>
                </li>
              ))}
            </ul>
          </div>

          {/* Ayah */}
          {story.ayah && (
            <div className="hv-card p-6" data-testid="story-ayah">
              <span className="hv-badge">{t("ayah")}</span>
              <div className="mt-4 text-right" dir="rtl">
                <div className="text-3xl sm:text-4xl leading-loose text-hv-navy font-arabic">{story.ayah.arabic}</div>
              </div>
              <div className="mt-3 text-hv-teal font-bold italic">{story.ayah.transliteration}</div>
              <div className="mt-1 text-hv-slate leading-relaxed">{getLoc(story.ayah.translations)}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-hv-slate">
                {story.ayah.surah} · {story.ayah.ayah_number}
              </div>
            </div>
          )}

          {/* Word of the Day */}
          {story.word_of_the_day && (
            <div className="hv-card p-6" data-testid="story-word">
              <span className="hv-badge hv-badge-coral">{t("word_of_the_day")}</span>
              <div className="mt-4 grid sm:grid-cols-3 gap-4 items-center">
                <div className="text-4xl text-hv-navy font-arabic text-center sm:text-start" dir="rtl">
                  {story.word_of_the_day.arabic}
                </div>
                <div className="text-hv-teal font-bold text-xl">{story.word_of_the_day.transliteration}</div>
                <div className="hv-heading text-hv-navy text-lg">{getLoc(story.word_of_the_day.meaning)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {learnSubTab === "quiz" && (
        <div className="mt-4 space-y-6" data-testid="story-quiz-tab">
          {/* Quiz — one question at a time behind a Start button, options
              reshuffled on every attempt, a running timer, and instant
              green/red feedback the moment an option is tapped. */}
          <div className="hv-card p-6" data-testid="story-quiz">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="hv-badge">{t("quiz")}</span>
              {quizStarted && quizShuffled.length > 0 && (
                // Stays on screen (frozen, not ticking) once quizResult is
                // set — the running interval already stops via the effect
                // above; this just keeps the same badge visible instead of
                // swapping it out, so "the timer stopped" is visually obvious
                // rather than the number just relocating into the result text.
                <span className={`hv-badge ${quizResult ? "" : "hv-badge-coral"}`} data-testid="quiz-timer">
                  <Clock size={12} /> {formatQuizTime(quizElapsed)}
                </span>
              )}
            </div>

            {!quizStarted ? (
              <div className="mt-6 text-center py-6">
                <p className="text-hv-slate text-sm mb-4">
                  {(story.quiz || []).length} questions · answered one at a time, against the clock
                </p>
                <button onClick={startQuiz} data-testid="quiz-start" className="hv-btn-primary">
                  Start Quiz
                </button>
              </div>
            ) : quizResult ? (
              <div className="mt-5 rounded-2xl bg-[#FBF6E4] border border-[#E5DFC8] p-5" data-testid="quiz-result">
                {quizResult.queued ? (
                  <div data-testid="quiz-queued">
                    <div className="hv-heading text-hv-navy text-xl">Answers saved</div>
                    <div className="mt-1 text-sm text-hv-slate">
                      You're offline. Your answers are saved on this device and will sync when you're back online.
                    </div>
                  </div>
                ) : quizResult.completed ? (
                  <>
                    <div className="hv-heading text-hv-navy text-xl flex items-center gap-2">
                      <Sparkles size={20} className="text-hv-gold" strokeWidth={2.5} /> {t("quiz_pass")}
                    </div>
                    <div className="mt-1 text-sm text-hv-slate">
                      {t("quiz_got")} {quizResult.correct} / {quizResult.total}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="hv-heading text-hv-navy text-xl">{t("quiz_almost")}</div>
                    <div className="mt-1 text-sm text-hv-slate">
                      {t("quiz_got")} {quizResult.correct} / {quizResult.total}
                    </div>
                  </>
                )}
                <button onClick={startQuiz} data-testid="quiz-retry" className="hv-btn-secondary mt-4 inline-flex items-center gap-2">
                  <Sparkles size={16} /> Retry Quiz
                </button>
              </div>
            ) : quizShuffled.length > 0 && (() => {
              const q = quizShuffled[quizIndex];
              return (
                <div className="mt-5" data-testid={`quiz-question-${quizIndex}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-hv-teal">
                      {QUIZ_CATEGORY_LABELS[q.category] || t("quiz")}
                    </span>
                    <span className="text-xs font-bold text-hv-slate">{quizIndex + 1} / {quizShuffled.length}</span>
                  </div>
                  <div className="mt-2 hv-heading text-hv-navy text-lg">{getLoc(q.question)}</div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-2">
                    {q.shuffledOptions.map((opt, pos) => {
                      const isCorrectOpt = quizRevealedCorrect !== null && opt.origIndex === quizRevealedCorrect;
                      const picked = quizPicked === pos;
                      let tone = "border-[#E5DFC8] bg-white hover:border-hv-teal";
                      if (quizPicked !== null && isCorrectOpt) tone = "border-hv-teal bg-[#E3F1EC]";
                      else if (picked) tone = "border-hv-coral bg-[#FDEAE6]";
                      return (
                        <button key={pos} onClick={() => pickQuizOption(pos)} disabled={quizPicked !== null}
                                data-testid={`quiz-q${quizIndex}-opt${pos}`}
                                className={`text-start rounded-2xl px-4 py-3 min-h-[44px] border-2 transition flex items-center gap-2 ${tone}`}>
                          {quizPicked !== null && isCorrectOpt && <CheckCircle2 size={18} className="text-hv-teal shrink-0" strokeWidth={2.5} />}
                          {picked && !isCorrectOpt && <span className="text-hv-coral font-bold shrink-0">✕</span>}
                          <span className="font-bold text-hv-navy">{opt.text}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* References */}
          {story.references?.length > 0 && (
            <div className="hv-card p-6" data-testid="story-references">
              <span className="hv-badge">{t("references")}</span>
              <ul className="mt-4 space-y-2">
                {story.references.map((r, i) => (
                  <li key={i} className="text-hv-navy"><span className="font-bold">{r.label}</span>
                    {r.link_or_note ? <span className="text-hv-slate"> — {r.link_or_note}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI Chat — Ask HikmahVerses */}
          <AskHikmahVerses storySeq={story.global_sequence} />
        </div>
      )}
        </div>
      )}
      {!tabLocked && (() => {
        const toTab = (t, sub) => () => { setTab(t); if (sub) setLearnSubTab(sub); window.scrollTo({ top: 0, behavior: "smooth" }); };
        let next = null;
        if (tab === "video") next = { label: "Mark complete · Next: Story Book", run: () => markStep("video", toTab("storybook")) };
        else if (tab === "storybook") next = { label: "Mark complete · Next: Morals", run: () => markStep("storybook", toTab("learn", "morals")) };
        else if (tab === "learn" && learnSubTab === "morals") next = { label: "Mark complete · Next: Quiz", run: () => markStep("morals", toTab("learn", "quiz")) };
        else if (tab === "learn" && learnSubTab === "quiz" && quizResult) next = { label: "Next: Write", run: () => markStep("quiz", toTab("write")) };
        else if (tab === "write") next = { label: "Mark complete", run: () => markStep("write", null) };
        if (!next) return null;
        return (
          <div className="mt-8 flex justify-end" data-testid="story-next-bar">
            <button type="button" onClick={next.run} data-testid="story-next-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-hv-coral text-white px-6 py-3 font-bold shadow-sm hover:brightness-105 active:scale-[0.98] transition">
              {next.label} <ArrowRight size={16} strokeWidth={2.6} />
            </button>
          </div>
        );
      })()}
    </main>
    </div>
  );
}
